import React, { useEffect, useState, lazy, Suspense } from 'react';
import { useResumeContext } from '../../context/ResumeContext';
import { portfolioData } from '../../data/portfolioData';

// Import portfolio images
import indigorgeousThumbnail from '../../assets/portfolio/indigorgeous-thumbnail.jpg';
import indigoEdgeThumbnail from '../../assets/portfolio/indigo-edge-thumbnail.jpg';
import presspectiveThumbnail from '../../assets/portfolio/presspective-thumbnail.jpg';
import printaxisThumbnail from '../../assets/portfolio/printaxis-thumbnail.jpg';
import duploManualThumbnail from '../../assets/portfolio/duplo-manual-thumbnail.jpg';
import foilSopThumbnail from '../../assets/portfolio/foil-sop-thumbnail.jpg';
import renderIntentThumbnail from '../../assets/portfolio/render-intent-thumbnail.jpg';
import calculatorSuiteThumbnail from '../../assets/portfolio/calculator-suite-thumbnail.jpg';
import spacePlanningThumbnail from '../../assets/portfolio/3d-space-planning-thumbnail.jpg';

// Lazy load the documentation components for better performance
const DuploDocumentation = lazy(() => 
  import('../documentation/DuploDocumentation')
);

// Map of project IDs to their thumbnail images
const thumbnailImages = {
  'indigorgeous': indigorgeousThumbnail,
  'indigo-edge': indigoEdgeThumbnail,
  'press-perspective': presspectiveThumbnail,
  'print-axis': printaxisThumbnail,
  'duplo-manual': duploManualThumbnail,
  'foil-sop': foilSopThumbnail,
  'render-intent': renderIntentThumbnail,
  'calculator-suite': calculatorSuiteThumbnail,
  '3d-space-planning': spacePlanningThumbnail
};

/**
 * Portfolio Module Component
 * Shows interactive learning tools and educational content
 */
const PortfolioModule = () => {
  const { markSectionViewed } = useResumeContext();
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeProject, setActiveProject] = useState(null);
  const [showInteractiveDemo, setShowInteractiveDemo] = useState(false);
  const [activeDemoId, setActiveDemoId] = useState(null);
  
  useEffect(() => {
    markSectionViewed('portfolio');
  }, [markSectionViewed]);

  // Get all unique project types for filtering
  const projectTypes = ['all', ...new Set(portfolioData.map(project => project.type))];

  // Filter projects based on active category
  const filteredProjects = activeCategory === 'all' 
    ? portfolioData 
    : portfolioData.filter(project => project.type === activeCategory);

  // Function to handle clicking a project card
  const handleProjectClick = (projectId) => {
    setActiveProject(projectId === activeProject ? null : projectId);
  };
  
  // Function to handle launching an interactive demo
  const handleLaunchDemo = (demoId) => {
    setActiveDemoId(demoId);
    setShowInteractiveDemo(true);
  };
  
  // Function to handle closing the interactive demo
  const handleCloseDemo = () => {
    setShowInteractiveDemo(false);
    setActiveDemoId(null);
  };
  
  // Loading spinner component for lazy-loaded content
  const LoadingSpinner = () => (
    <div className="flex items-center justify-center h-screen">
      <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
      <p className="ml-4 text-lg text-blue-600">Loading interactive documentation...</p>
    </div>
  );

  return (
    <div className="space-y-6">
      {showInteractiveDemo && activeDemoId === 'duplo-documentation' ? (
        <div className="interactive-demo-container">
          <div className="mb-4 flex justify-between items-center">
            <h2 className="text-xl font-bold text-blue-700 flex items-center gap-2">
              <button 
                onClick={handleCloseDemo}
                className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 flex items-center gap-1"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Portfolio
              </button>
            </h2>
          </div>
          
          <Suspense fallback={<LoadingSpinner />}>
            <DuploDocumentation />
          </Suspense>
        </div>
      ) : (
        <>
          <section className="card border-l-4 border-blue-500">
            <h2 className="text-xl font-bold text-blue-700 mb-4 flex items-center gap-2">
              💼 Interactive Learning Tools Portfolio
            </h2>
            <p className="mb-6">
              The projects below showcase my work in creating interactive learning tools, 
              technical documentation, and educational content. Each example demonstrates 
              my approach to transforming complex information into engaging, accessible learning experiences.
            </p>
          </section>

          {/* Project Filters */}
          <div className="card">
            <h3 className="text-lg font-bold mb-4">Filter by Project Type</h3>
            <div className="flex flex-wrap gap-2">
              {projectTypes.map(type => (
                <button
                  key={type}
                  onClick={() => setActiveCategory(type)}
                  className={`px-3 py-1 rounded-full text-sm ${
                    activeCategory === type
                      ? 'bg-blue-500 text-white'
                      : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                  }`}
                >
                  {type === 'all' ? 'All Projects' : type}
                </button>
              ))}
            </div>
            
            <div className="mt-4 text-sm text-slate-600">
              Showing {filteredProjects.length} {filteredProjects.length === 1 ? 'project' : 'projects'}
              {activeCategory !== 'all' && ` in ${activeCategory}`}
            </div>
          </div>

          {/* Project Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredProjects.map((project) => (
              <div 
                key={project.id}
                className={`card hover:shadow-lg transition-shadow cursor-pointer ${
                  activeProject === project.id ? 'ring-2 ring-blue-500' : ''
                }`}
                onClick={() => handleProjectClick(project.id)}
              >
                <div className="relative">
                  {/* Project Type Badge */}
                  <div className="absolute top-0 right-0">
                    <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                      {project.type}
                    </span>
                  </div>
                  
                  {/* Project Thumbnail */}
                  <div className="h-48 overflow-hidden rounded-t-lg bg-slate-100 flex items-center justify-center mb-4">
                    <img 
                      src={thumbnailImages[project.id] || `https://via.placeholder.com/400x200?text=${project.title.replace(/ /g, '+')}`}
                      alt={`${project.title} thumbnail`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  <h3 className="text-lg font-bold mb-2">{project.title}</h3>
                  <p className="text-slate-600 line-clamp-3 mb-2">
                    {project.description}
                  </p>
                  
                  {/* Skills Tags */}
                  <div className="flex flex-wrap gap-1 mb-4">
                    {project.skills.map((skill) => (
                      <span 
                        key={skill} 
                        className="inline-block px-2 py-0.5 bg-slate-100 text-slate-700 text-xs rounded"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                  
                  <div className="flex items-center text-sm text-blue-600">
                    <span>View Details</span>
                    <span className="ml-1">{activeProject === project.id ? '▲' : '▼'}</span>
                  </div>
                </div>
                
                {/* Expanded Details */}
                {activeProject === project.id && (
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="font-bold mb-2">Key Highlights</h4>
                    <ul className="space-y-2 mb-4">
                      {project.highlights.map((highlight, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="text-blue-500">→</span>
                          <span>{highlight}</span>
                        </li>
                      ))}
                    </ul>
                    
                    {/* Project Links */}
                    <div className="flex flex-wrap gap-2">
                      {project.hasInteractiveDemo && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLaunchDemo(project.urls["Interactive Version"].replace('internal:', ''));
                          }}
                          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm"
                        >
                          Launch Interactive Demo
                        </button>
                      )}
                      {Object.entries(project.urls)
                        .filter(([label]) => !project.hasInteractiveDemo || label !== "Interactive Version")
                        .map(([label, url]) => (
                        <a
                          key={label}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`px-4 py-2 rounded transition-colors text-sm ${
                            label === 'demo' || label === 'Explore' 
                              ? 'bg-blue-600 text-white hover:bg-blue-700'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                          }`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {label === 'demo' ? 'Demo / Preview' : 
                           label === 'details' ? 'Case Study' :
                           label === 'code' ? 'View Code' :
                           label}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Learning Focus Areas */}
          <section className="card">
            <h3 className="text-lg font-bold mb-4">Learning Focus Areas</h3>
            <p className="text-slate-600 mb-4">
              My portfolio projects reflect my focus on specific educational approaches and learning objectives.
              Each project is designed with these principles in mind:
            </p>
            
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                <h4 className="font-bold text-blue-800 mb-2">Visual Learning Enhancement</h4>
                <p className="text-sm">
                  Creating visual aids, diagrams, and interactive elements that clarify abstract concepts
                  and make complex technical information more accessible.
                </p>
              </div>
              
              <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                <h4 className="font-bold text-green-800 mb-2">Progressive Knowledge Building</h4>
                <p className="text-sm">
                  Structuring information to build progressively from fundamental concepts to advanced
                  applications, creating a scaffolded learning experience.
                </p>
              </div>
              
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                <h4 className="font-bold text-purple-800 mb-2">Interactive Engagement</h4>
                <p className="text-sm">
                  Incorporating hands-on elements, practical applications, and interactive tools
                  that reinforce learning through active participation.
                </p>
              </div>
            </div>
          </section>
          
          {/* Learning Impact */}
          <section className="card">
            <h3 className="text-lg font-bold mb-4">Learning Impact</h3>
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-100 mb-4">
              <p className="italic text-yellow-800">
                "Alex is able to wear many hats at once, and be incredibly efficient and successful while doing so. 
                I was also able to utilize him as a trainer for new team members, and was confident in his 
                ability to be thorough and consistent with his training."
              </p>
              <p className="text-right text-sm font-medium text-yellow-700 mt-2">
                — Quinn Matchette, Production Manager, Indigo Ink Digital Printing
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4">
              <div className="border rounded-lg p-4">
                <div className="text-center mb-2">
                  <span className="text-3xl font-bold text-blue-600">75%</span>
                </div>
                <p className="text-sm text-center">
                  Reduction in training time for new equipment operators through improved documentation
                </p>
              </div>
              
              <div className="border rounded-lg p-4">
                <div className="text-center mb-2">
                  <span className="text-3xl font-bold text-blue-600">90%</span>
                </div>
                <p className="text-sm text-center">
                  Of workshop participants reported improved understanding of technical concepts
                </p>
              </div>
              
              <div className="border rounded-lg p-4">
                <div className="text-center mb-2">
                  <span className="text-3xl font-bold text-blue-600">43%</span>
                </div>
                <p className="text-sm text-center">
                  Increase in client adoption of advanced techniques after educational content implementation
                </p>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default PortfolioModule;
